import type { DSData } from 'webapp-types';
import { DataSelectType } from 'webapp/enums/enums';

import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

export interface SeriesData {
	type: DataSelectType;
	points?: DChartPrimaryPoint[];
	timezone?: string;
	title?: string;
	titlePrefix?: string;
	typeToDraw?: string;
	granulationLabels?: DSData.GranulationLabel[];
	axisIndex?: number | null;
	isStacked?: boolean;
	showDataLabels?: boolean;
	baseId?: string;
	metricSelectionData?: MetricSelectionData;
}

export const FALLBACK_COLOR_INDEX = 99;

export default class Series {
	nodeType = true; // hack so highcharts skips trying to reach all references of this in its cleanRecursivly method

	isTheAggregatedTotal = false; // this series is an aggregation of other series

	idForHc = ''; // updated in grid renderer when building data obj, used in tooltip

	points: DChartPrimaryPoint[];

	// this is used to flag series that needs their threshold updated after-chart-render
	// since series.threshold itself is variable even after setting it, we track it here instead of highcharts settings itself
	updateDynamicThresholdFrom: 'min' | 'max' | null = null;

	type: DataSelectType;
	timezone: string;
	title: string;
	titlePrefix: string; // only need to pass for Compares (the actual compare titleUX)
	typeToDraw: string;
	granulationLabels: DSData.GranulationLabel[];
	axisIndex: number;
	isStacked: boolean;
	showDataLabels: boolean;
	baseId: string;
	metricSelectionData: MetricSelectionData | null; // if null, it might mean it's not from the same output (e.g. funnel - series points are from different metrics)

	// number if from theme, string (custom-color-x) if user-custom
	// this will be reflected in the classes
	private _colorIndexOrClass: string | number = FALLBACK_COLOR_INDEX;

	primarySeries: Series | null = null; // optionally set when creating Series

	constructor(seriesData: SeriesData) {
		this.type = seriesData.type;

		this.timezone = seriesData.timezone || '';
		this.title = seriesData.title || '';
		this.titlePrefix = seriesData.titlePrefix || Series.getTitleFromType(seriesData.type);
		this.typeToDraw = seriesData.typeToDraw || '';
		this.granulationLabels = seriesData.granulationLabels || [];
		this.axisIndex = seriesData.axisIndex || 0;
		this.isStacked = !!seriesData.isStacked;
		this.showDataLabels = !!seriesData.showDataLabels;
		this.baseId = seriesData.baseId || '';
		this.points = seriesData.points || [];

		this.metricSelectionData = seriesData.metricSelectionData || null;

		// set series ref on points
		this.points.forEach((p) => {
			p.series = this;
		});
	}

	get colorIndexOrClass() {
		return this._colorIndexOrClass;
	}

	set colorIndexOrClass(value: string | number) {
		this._colorIndexOrClass = value;
		this.points.forEach((p) => (p.colorIndexOrClass = value));
	}

	get hasNoColorIndexOrClass() {
		return this.colorIndexOrClass === FALLBACK_COLOR_INDEX;
	}

	get isMain() {
		return this.type === DataSelectType.MAIN;
	}

	get isCompare() {
		return this.type === DataSelectType.COMPARE;
	}

	get isAverage() {
		return this.type === DataSelectType.AVERAGE;
	}

	get isTrend() {
		return this.type === DataSelectType.TREND;
	}

	get isMedian() {
		return this.type === DataSelectType.MEDIAN;
	}

	get isGoal() {
		return this.type === DataSelectType.GOAL;
	}

	get isAnomaly() {
		return this.type === DataSelectType.ANOMALY;
	}

	get isForecast() {
		return this.type === DataSelectType.FORECAST;
	}

	get isForecastOptimistic() {
		return this.type === DataSelectType.FORECAST_OPTIMISTIC;
	}

	get isForecastPessimistic() {
		return this.type === DataSelectType.FORECAST_PESSIMISTIC;
	}

	get isOneOfForecasts(): boolean {
		return this.isForecast || this.isForecastOptimistic || this.isForecastPessimistic;
	}

	get isCompareWithoutPrimary() {
		return this.isCompare && !this.primarySeries;
	}

	get isAvgTrendMedianOrGoal() {
		return this.isAverage || this.isTrend || this.isMedian || this.isGoal;
	}

	get stackId() {
		return `stack-${this.typeToDraw}-${this.type}`;
	}

	get hasTooltip() {
		return !this.isAverage && !this.isTrend && !this.isMedian && !this.isTheAggregatedTotal;
	}

	get isSingleValue() {
		return this.points.filter((p) => p.hasValue).length === 1;
	}

	get isBar() {
		return this.typeToDraw === 'column';
	}

	get titleForLegendShort() {
		return this.titlePrefix || this.title;
	}

	get titleForLegend() {
		let title = this.title;
		const prefix = this.titlePrefix;
		if (prefix) {
			title = `${prefix}: ${title}`;
		}
		return title;
	}

	get periodEndTS(): number {
		const labels = this.granulationLabels;
		return labels[labels.length - 1]?.endTs || 0;
	}

	get granulationTimeUnit() {
		return this.points[0]?.dateRange?.granulation?.timeUnit;
	}

	getFirstPointWithValue() {
		return this.points.find((p) => p.hasValue);
	}

	getLastPointWithValue() {
		return Array.from(this.points)
			.reverse()
			.find((p) => p.hasValue);
	}

	static getTitleFromType(type: DataSelectType) {
		let title = '';
		switch (type) {
			case DataSelectType.COMPARE:
				title = 'Compare';
				break;
			case DataSelectType.AVERAGE:
				title = 'Average';
				break;
			case DataSelectType.TREND:
				title = 'Trend';
				break;
			case DataSelectType.MEDIAN:
				title = 'Median';
				break;
			case DataSelectType.GOAL:
				title = 'Goal';
				break;
			case DataSelectType.FORECAST:
				title = 'Forecast';
				break;
			case DataSelectType.FORECAST_OPTIMISTIC:
				title = 'Forecast (optimistic)';
				break;
			case DataSelectType.FORECAST_PESSIMISTIC:
				title = 'Forecast (pessimistic)';
				break;
		}
		return title;
	}

	static getMinAndMaxValues(values: (number | null)[]): { min: number; max: number } {
		let min = Infinity,
			max = -Infinity;

		values.forEach((value) => {
			if (typeof value === 'number') {
				if (value < min) {
					min = value;
				}
				if (value > max) {
					max = value;
				}
			}
		});

		if (!isFinite(min)) {
			min = NaN;
		}

		if (!isFinite(max)) {
			max = NaN;
		}

		return { min, max };
	}

	static getFirstPointsFromMainSeries(series: Series[]) {
		const points: DChartPrimaryPoint[] = [];

		series
			.filter((serie) => serie.isMain)
			.forEach((serie) => {
				if (serie.points.length) {
					points.push(serie.points[0]!);
				}
			});

		return points;
	}
}
